import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useWebSocket from 'react-use-websocket';
import { CheckCircle, Feather, XOctagon } from 'react-feather';
import config from '../config.json';
import { capitalizeFirstLetter, numberWithCommas } from './utils';

// Modal Components
import AcceptModal from './AcceptModal';
import ApprovalCallbackModal from './ApprovalCallbackModal';
import DeclineCallbackModal from './DeclineCallbackModal';
import StipSheetModal from './StipSheetModal';
import CustomerInfoModal from './CustomerInfoModal';
import PostSubmitModal from './PostSubmitModal';
import PreviousStructuresModal from './PreviousStructuresModal';
import VehicleInfoModal from './VehicleInfoModal';
import ChatModal from './ChatModal';
import ContactUsModal from './ContactUsModal';

const apiURL = window.location.hostname === '192.168.1.45' 
    ? 'http://localhost:8383/dealerrehash'
    : 'https://api.galeforceai.com/dealerrehash';
const WS_URL = 'wss://6aiolymuhf.execute-api.us-east-2.amazonaws.com/prod';

const Rehash = () => {
    const navigate = useNavigate();
    const { appNum } = useParams();
    const [dealerId] = useState(localStorage.getItem('selected_dealer_id') ? localStorage.getItem('selected_dealer_id') : localStorage.getItem('dealer_id'));
    const [userId] = useState(localStorage.getItem('user_id'));
    const [token] = useState(localStorage.getItem('heimdall'));
    const [firstName] = useState(localStorage.getItem('first_name'));
    const [lastName] = useState(localStorage.getItem('last_name'));
    const [acceptedApplication, setAcceptedApplication] = useState(false);
    const [fundingApplication, setFundingApplication] = useState(false);
    const [modifiedApplication, setModifiedApplication] = useState(false);
    const [modifiedVehicle, setModifiedVehicle] = useState(false);
    const [submittedApplication, setSubmittedApplication] = useState(false);
    const [showProgress, setShowProgress] = useState(true);
    const [calculatedMaxAdvance, setCalculatedMaxAdvance] = useState('');
    const [calculatedAmountFinanced, setCalculatedAmountFinanced] = useState('');
    const [dealStips, setDealStips] = useState([]);
    const [dealerNotes, setDealerNotes] = useState([]);
    const [dealerState, setDealerState] = useState('');
    const [currentVin, setCurrentVin] = useState('');
    const [currentMileage, setCurrentMileage] = useState('');
    const [currentVehicleDescription, setCurrentVehicleDescription] = useState('');
    const [newVehicleYear, setNewVehicleYear] = useState('');
    const [newVehicleMake, setNewVehicleMake] = useState('');
    const [newVehicleModel, setNewVehicleModel] = useState('');
    const commentsContent = useRef(null);
    const [chatMessages, setChatMessages] = useState([{ 'date_time': '', 'user_id': '', 'first_name': '', 'last_name': '', 'message': '' }]);
    const [serverMessage, setServerMessage] = useState('');
    const [fundingStatusArray] = useState(['Contract Received', 'Verifications Incomplete', 'Ready to Book', 'Booked']);
    const [appData, setAppData] = useState({
        'rec_app_first_name': '',
        'rec_app_last_name': '',
        'rec_app_streetno': '',
        'rec_app_streetname': '',
        'rec_app_aptno': '',
        'rec_app_city': '',
        'rec_app_state': '',
        'rec_app_zip': '',
        'rec_app_addr_years': '',
        'rec_app_addr_months': '',
        'rec_app_emp_name': '',
        'rec_app_emp_total_months': '',
        'rec_app_emp_years': '',
        'rec_app_emp_months': '',
        'rec_app_income': '',
        'rec_app_other_income_source': '',
        'rec_app_other_income': '',
        'rec_app_ttl_income': '',
        'rec_app_pti': '',
        'rec_coapp_first_name': '',
        'rec_coapp_last_name': '',
        'rec_coapp_streetno': '',
        'rec_coapp_streetname': '',
        'rec_coapp_aptno': '',
        'rec_coapp_city': '',
        'rec_coapp_state': '',
        'rec_coapp_zip': '',
        'rec_coapp_addr_years': '',
        'rec_coapp_addr_months': '',
        'rec_coapp_emp_name': '',
        'rec_coapp_emp_total_months': '',
        'rec_coapp_emp_years': '',
        'rec_coapp_emp_months': '',
        'rec_coapp_income': '',
        'rec_coapp_other_income_source': '',
        'rec_coapp_other_income': '',
        'rec_application_number': '',
        'rec_app_status': '',
        'req_sales_price': '',
        'rec_sales_price': '',
        'estimated_sales_tax': '',
        'estimated_ttl': '',
        'req_cash_down': '',
        'rec_cash_down': '',
        'req_net_trade': '',
        'rec_net_trade': '',
        'req_unpaid_balance': '',
        'rec_unpaid_balance': '',
        'req_backend_allowance': '',
        'rec_backend_allowance': '',
        'req_gap': '',
        'rec_gap': '',
        'rec_gap_dca_max': '',
        'rec_credit_life_ins': '',  // rec_fldocstamptaxUW
        'req_amt_financed': '',
        'rec_est_amt_financed': '',
        'rec_calc_discount': '',
        'req_rate': '',
        'req_system_buyrate': '',
        'rec_buyrate': '',
        'rec_orig_fee': '',
        'rec_dealer_proceeds': '',
        'req_term_months': '',
        'rec_term_months': '',
        'req_payment': '',
        'rec_payment': '',
        'rec_payment_freq': '',
        'rec_line3ltv': '',  // LTV without Gap
        'rec_ltv_ratio': '',  // LTV with Gap
        'rec_dealer_id': '',
        'rec_dealer_name': '',
        'rec_dealer_citystatezip': '',
        'rec_dealer_email': '',
        'rec_dealer_phone': '',
        'rec_UW_collateral_desc': '',
        'rec_veh_year': '',
        'rec_veh_make': '',
        'rec_veh_model': '',
        'rec_veh_trim': '',
        'rec_veh_mileage': '',
        'rec_vin': '',
        'rec_veh_bookout_value': '',
        'vehicle_age': '',
        'rec_tier': '',
        'rec_buyer_tier': '',
        'min_income': '',
        'max_pti': '',
        'max_ltv': '',
        'max_amt_fin_less_gap': '',
        'max_term': '',
        'max_payment': '',
        'max_miles': '',
        'max_down_payment': '',
        'min_toj_months': '',
        'rec_AS_indicator': '',
        'accept_deal': '',
        'dealer_portal_app': '',
        'app_received_date': '',
        'app_decision_date': '',
        'cr_received_date': '',
        'vi_entry_date': '',
        'vi_end_date': '',
        'rec_dealer_branch': '',
        'rec_analyst_fullname': '',
        'deal_stips': '',
        'dealer_notes': ''
    });

    useEffect(() => {
        localStorage.setItem('env', window.location.href.includes('usautocreditportal') ? '/prod' : 'qa');
        document.title = 'Rehash Application';
        // checkLogin();
        fetchData();
        getComments();
        window.scrollTo(0, 0);
    }, []);

    // const { getWebSocket } = useWebSocket(WS_URL + '?userId=' + userId + '&applicationNumber=' + applicationNumber, {
    const { getWebSocket } = useWebSocket(WS_URL + '?sourceSystemUrl=' + window.location.href + '&userId=' + userId + '&appNum=' + appNum, {
        onOpen: () => console.log('Connected!'),
        onMessage: (event) => {
            setServerMessage(event.data);
            setPostedNewChatMessage(false);
            setSubmittedApplication(false);
            fetchData();
            getComments();
        },
        shouldReconnect: (closeEvent) => true,
    });

    const scrollToBottom = () => {
        if (commentsContent && commentsContent.current) {
            commentsContent.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const checkLogin = () => {
        if (localStorage.getItem('heimdall') === null || String(localStorage.getItem('heimdall')).length !== 32) {
            navigate('/login');
        }
    };

    const decodeVin = (vin) => {
        fetch('https://vpic.nhtsa.dot.gov/api/vehicles/decodevin/' + vin + '?format=json', {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            }
        })
            .then((response) => response.json())
            .then((response) => {
                console.log(response.Results[10].Value);
                console.log(response.Results[7].Value);
                console.log(response.Results[9].Value);
                setNewVehicleYear(response.Results[10].Value);
                setNewVehicleMake(capitalizeFirstLetter(response.Results[7].Value));
                setNewVehicleModel(response.Results[9].Value);
                setAppData(values => ({
                    ...values,
                    ['rec_UW_collateral_desc']:
                        response.Results[10].Value + ' ' +
                        capitalizeFirstLetter(response.Results[7].Value) + ' ' +
                        response.Results[9].Value
                }));
            })
            .catch(function (err) {
                console.log('Error:', err);
            });
    };

    const fetchData = () => {
        fetch(apiURL + `/application/${appNum}`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Authorization': config.dealerrehashapplicationdetails,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                // 'app_num': appNum,
                'dealer_id': dealerId ? dealerId : '0',
                'user_id': userId ? userId : '0',
                'token': token
            })
        })
            .then((response) => response.json())
            .then((response) => {
                setAppData(response);

                if (response.accept_deal && response.accept_deal.toLowerCase() === 'yes') {
                    setAcceptedApplication(true);
                }

                if (['RA for Payment Call', 'Review Required', 'Return to UW'].includes(response.rec_app_status)) {
                    setSubmittedApplication(true);
                }

                if (fundingStatusArray.includes(response.rec_app_status)) {
                    setFundingApplication(true);
                }

                if (response.deal_stips) {
                    const stipsObject = JSON.parse(response.deal_stips);
                    setDealStips(stipsObject.DealStips);
                }

                if (response.dealer_notes) {
                    const dealerNotesObject = JSON.parse(response.dealer_notes);
                    setDealerNotes(dealerNotesObject.DealerNotes);
                }

                if (response.rec_vin) {
                    setCurrentVin(response.rec_vin);
                }

                if (response.rec_veh_mileage) {
                    setCurrentMileage(response.rec_veh_mileage);
                }

                if (!response.rec_dealer_citystatezip || (response.rec_dealer_citystatezip && (response.rec_dealer_citystatezip.includes(' FL') || response.rec_dealer_citystatezip.includes('FL ')))) {
                    setDealerState('FL');
                } else {
                    setDealerState('');
                }

                if (response.rec_UW_collateral_desc) {
                    setCurrentVehicleDescription(response.rec_UW_collateral_desc);
                }

                setCalculatedMaxAdvance((parseFloat(response.req_sales_price) - parseFloat(response.req_cash_down) - parseFloat(response.req_net_trade)).toString());
                setCalculatedAmountFinanced((parseFloat(response.req_sales_price) - parseFloat(response.req_cash_down) - parseFloat(response.req_net_trade) + parseFloat(response.req_gap ? response.req_gap : '0') + parseFloat(response.rec_credit_life_ins)).toString());
                setShowProgress(false);
            })
            .catch(function (err) {
                console.log('Error:', err);
                setShowProgress(false);
            });
    };

    const getComments = () => {
        fetch(apiURL + '/chatmessagelist', {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Authorization': config.dealer_chat_messages_get_token,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                'app_num': appNum,
                'dealer_id': dealerId ? dealerId : '0',
                'user_id': userId ? userId : '0',
                'token': token,
            })
        })
            .then((response) => response.json())
            .then((response) => {
                // Ensure chatMessages is always an array
                if (Array.isArray(response)) {
                    setChatMessages(response);
                } else if (response.messages && Array.isArray(response.messages)) {
                    // If response has a messages property that's an array
                    setChatMessages(response.messages);
                } else {
                    // Fallback to empty array if response doesn't contain expected data
                    console.log('Chat response is not in expected format:', response);
                    setChatMessages([]);
                }
            })
            .catch(function (err) {
                console.log('Error:', err);
                setChatMessages([]);
            });
        
        scrollToBottom();
    };

    function fancyTooltip(labelId) {
        const popup = document.getElementById(labelId);
        if (popup) {
            popup.classList.toggle("show");
        }
    }

    function updateApplication(dealAccepted) {
        setAcceptedApplication(dealAccepted);
        setSubmittedApplication(dealAccepted ? false : true);
        setModifiedApplication(false);
        setModifiedVehicle(false);
        setServerMessage('');
        
        fetch('https://9kd9sezz5a.execute-api.us-east-2.amazonaws.com' + (localStorage.getItem('env') === '/prod' ? '/prod' : '') + '/dealerpublishupdatedapplication', {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Authorization': 
                    config.dealer_publish_updated_application_token + '/' + 
                    (dealerId ? dealerId : '0') + '/' + 
                    (userId ? userId : '0') + '/' + 
                    (token ? token : '0'),
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                'rec_application_number': appData.rec_application_number ? appData.rec_application_number : '',
                'rec_app_first_name': appData.rec_app_first_name ? appData.rec_app_first_name : '',
                'rec_app_last_name': appData.rec_app_last_name ? appData.rec_app_last_name : '',
                'rec_app_streetno': appData.rec_app_streetno ? appData.rec_app_streetno : '',
                'rec_app_streetname': appData.rec_app_streetname ? appData.rec_app_streetname : '',
                'rec_app_aptno': appData.rec_app_aptno ? appData.rec_app_aptno : '',
                'rec_app_city': appData.rec_app_city ? appData.rec_app_city : '',
                'rec_app_state': appData.rec_app_state ? appData.rec_app_state : '',
                'rec_app_zip': appData.rec_app_zip ? appData.rec_app_zip : '',
                'rec_app_addr_years': appData.rec_app_addr_years ? appData.rec_app_addr_years : '',
                'rec_app_addr_months': appData.rec_app_addr_months ? appData.rec_app_addr_months : '',
                'rec_app_emp_name': appData.rec_app_emp_name ? appData.rec_app_emp_name : '',
                'rec_app_emp_years': appData.rec_app_emp_years ? appData.rec_app_emp_years : '',
                'rec_app_emp_months': appData.rec_app_emp_months ? appData.rec_app_emp_months : '',
                'rec_app_income': appData.rec_app_income ? appData.rec_app_income : '',
                'rec_app_other_income_source': appData.rec_app_other_income_source ? appData.rec_app_other_income_source : '',
                'rec_app_other_income': appData.rec_app_other_income ? appData.rec_app_other_income : '',
                'rec_coapp_first_name': appData.rec_coapp_first_name ? appData.rec_coapp_first_name : '',
                'rec_coapp_last_name': appData.rec_coapp_last_name ? appData.rec_coapp_last_name : '',
                'rec_coapp_streetno': appData.rec_coapp_streetno ? appData.rec_coapp_streetno : '',
                'rec_coapp_streetname': appData.rec_coapp_streetname ? appData.rec_coapp_streetname : '',
                'rec_coapp_aptno': appData.rec_coapp_aptno ? appData.rec_coapp_aptno : '',
                'rec_coapp_city': appData.rec_coapp_city ? appData.rec_coapp_city : '',
                'rec_coapp_state': appData.rec_coapp_state ? appData.rec_coapp_state : '',
                'rec_coapp_zip': appData.rec_coapp_zip ? appData.rec_coapp_zip : '',
                'rec_coapp_addr_years': appData.rec_coapp_addr_years ? appData.rec_coapp_addr_years : '',
                'rec_coapp_addr_months': appData.rec_coapp_addr_months ? appData.rec_coapp_addr_months : '',
                'rec_coapp_emp_name': appData.rec_coapp_emp_name ? appData.rec_coapp_emp_name : '',
                'rec_coapp_emp_years': appData.rec_coapp_emp_years ? appData.rec_coapp_emp_years : '',
                'rec_coapp_emp_months': appData.rec_coapp_emp_months ? appData.rec_coapp_emp_months : '',
                'rec_coapp_income': appData.rec_coapp_income ? appData.rec_coapp_income : '',
                'rec_coapp_other_income_source': appData.rec_coapp_other_income_source ? appData.rec_coapp_other_income_source : '',
                'rec_coapp_other_income': appData.rec_coapp_other_income ? appData.rec_coapp_other_income : '',
                'req_sales_price': appData.req_sales_price ? appData.req_sales_price : '',
                'rec_sales_price': appData.req_sales_price ? appData.req_sales_price : '',  // Note: rec = req
                'req_cash_down': appData.req_cash_down ? appData.req_cash_down : '',
                'rec_cash_down': appData.req_cash_down ? appData.req_cash_down : '',
                'req_net_trade': appData.req_net_trade ? appData.req_net_trade : '',
                'rec_net_trade': appData.req_net_trade ? appData.req_net_trade : '',
                'req_unpaid_balance': appData.req_unpaid_balance ? appData.req_unpaid_balance : '',
                'rec_unpaid_balance': calculatedMaxAdvance ? calculatedMaxAdvance : '',
                'req_gap': appData.req_gap ? appData.req_gap : '',
                'req_rate': appData.req_rate ? appData.req_rate : '',
                'req_term_months': appData.req_term_months ? appData.req_term_months : '',
                'rec_term_months': appData.req_term_months ? appData.req_term_months : '',
                'req_payment': appData.req_payment ? appData.req_payment : '',
                'rec_payment': appData.req_payment ? appData.req_payment : '',
                'rec_payment_freq': appData.rec_payment_freq ? appData.rec_payment_freq : '',
                'rec_vin': appData.rec_vin ? appData.rec_vin : '',
                'rec_veh_mileage': appData.rec_veh_mileage ? appData.rec_veh_mileage : '',
                'rec_AS_indicator': 'DP',
                'accept_deal': dealAccepted ? 'Yes' : 'No',
                'dealer_portal_app': 'Yes',
                'rec_app_status': appData.rec_app_status ? appData.rec_app_status : '',
                'env': localStorage.getItem('env') ? localStorage.getItem('env') : ''
            })
        })
            .then((response) => response.json())
            .then((response) => {
                console.log(response);
            })
            .catch(function (err) {
                console.log('Error:', err);
            });
    }

    const handleChange = (event) => {
        const name = event.target.name;
        const value = event.target.value;

        setAppData(values => ({ ...values, [name]: value }));
        setModifiedApplication(true);

        // Ensure req_gap exists
        if (!appData.req_gap) {
            appData.req_gap = '0';
        }

        // Update calculated values based on changes
        if (name === 'req_sales_price') {
            setCalculatedMaxAdvance((parseFloat(value) - parseFloat(appData.req_cash_down) - parseFloat(appData.req_net_trade)).toString());
            setCalculatedAmountFinanced((parseFloat(value) - parseFloat(appData.req_cash_down) - parseFloat(appData.req_net_trade) + parseFloat(appData.req_gap) + parseFloat(appData.rec_credit_life_ins)).toString());
        } else if (name === 'req_cash_down') {
            setCalculatedMaxAdvance((parseFloat(appData.req_sales_price) - parseFloat(value) - parseFloat(appData.req_net_trade)).toString());
            setCalculatedAmountFinanced((parseFloat(appData.req_sales_price) - parseFloat(value) - parseFloat(appData.req_net_trade) + parseFloat(appData.req_gap) + parseFloat(appData.rec_credit_life_ins)).toString());
        } else if (name === 'req_net_trade') {
            setCalculatedMaxAdvance((parseFloat(appData.req_sales_price) - parseFloat(appData.req_cash_down) - parseFloat(value)).toString());
            setCalculatedAmountFinanced((parseFloat(appData.req_sales_price) - parseFloat(appData.req_cash_down) - parseFloat(value) + parseFloat(appData.req_gap) + parseFloat(appData.rec_credit_life_ins)).toString());
        } else if (name === 'req_gap') {
            setCalculatedAmountFinanced((parseFloat(appData.req_sales_price) - parseFloat(appData.req_cash_down) - parseFloat(appData.req_net_trade) + parseFloat(value) + parseFloat(appData.rec_credit_life_ins)).toString());
        }

        // Handle VIN lookup
        if (name === 'rec_vin' && value.length === 17) {
            decodeVin(value);
            if (value !== currentVin) {
                setModifiedVehicle(true);
            }
        }
    };

    const handleSelectChange = (event) => {
        const name = event.target.name;
        const value = event.target.value;
        setAppData(values => ({ ...values, [name]: value }));
        setModifiedApplication(true);
    };

    // Chat-related state and functions
    const [postedNewChatMessage, setPostedNewChatMessage] = useState(false);

    return (
        <>
            <div className="container-xl col-12 px-4 mt-4">
                <div className="row">
                    <div className="col-xl-8">
                        {/* Sub-Nav card */}
                        <div className="card mb-2">
                            <div className="card-body">
                                <ul className="nav nav-pills" id="pills-tab" role="tablist">
                                    <li className="nav-item" role="presentation">
                                        <button className="nav-link main_nav active" id="pills-structure-tab" data-bs-toggle="pill" data-bs-target="#pills-structure" type="button" role="tab" aria-controls="pills-structure" aria-selected="false">Structure</button>
                                    </li>
                                    <li className="nav-item" role="presentation">
                                        <button className="nav-link main_nav" type="button" data-bs-toggle="modal" data-bs-target="#comments_modal">Submit Comments</button>
                                    </li>
                                    <li className="nav-item" role="presentation">
                                        <button className="nav-link main_nav" type="button" data-bs-toggle="modal" data-bs-target="#contact_us_modal">Contact Us</button>
                                    </li>
                                </ul>
                            </div>
                        </div>
                        <div className="tab-content" id="pills-tabContent">
                            <div className="tab-pane fade show active" id="pills-structure" role="tabpanel" aria-labelledby="pills-structure-tab">
                                <div className="row">
                                    <div className="col-xl-12">
                                        {/* Deal Structure */}
                                        <div className="card mb-1">
                                            <div className="card-header">
                                                {appData.rec_app_first_name ? 'Deal Structure for ' + appData.rec_app_first_name + ' ' + appData.rec_app_last_name : 'Invalid Application'}
                                                {showProgress ? <img src="/images/rainbow-spinner.gif" alt="spinner" id="progressindicator" height="10px" style={{ marginLeft: "5px" }} /> : ''}
                                                {modifiedVehicle ? <span style={{ color: 'darkorange', fontSize: '12px', marginLeft: '15px' }}> Vehicle changed. Sales Price should be adjusted.</span> : ''}
                                                <div className="App_Btn">
                                                    <button 
                                                        className="btn btn-sm btn-success" 
                                                        style={{ paddingLeft: "45px", paddingRight: "45px" }} 
                                                        data-bs-toggle="modal" 
                                                        data-bs-target="#accept_modal"
                                                        disabled={acceptedApplication || fundingApplication || submittedApplication || !appData.rec_application_number || (appData.rec_app_status !== 'Approved' && appData.rec_app_status !== 'Conditioned')}
                                                        title="Accept this approval and start the funding process." 
                                                        onClick={() => updateApplication(true)}
                                                    >
                                                        Accept
                                                    </button>
                                                    &nbsp;&nbsp;&nbsp;
                                                    {/* <button className="btn btn-sm btn-primary" data-bs-toggle="modal" data-bs-target="#structures">Previous Structures</button> */}
                                                </div>
                                            </div>
                                            <div className="card-body" id="deal-structure-body">
                                                <div className="row">
                                                    <div className="col-sm-6">
                                                        <form>
                                                            <input type="hidden" value="autocompleteworkaround" />
                                                            <div className="form-group row">
                                                                <h6 className="col-12"><i className="material-icons text-info pr-2 mr-2">Requested</i> Structure</h6>
                                                            </div>
                                                        </form>
                                                    </div>
                                                    
                                                    <div className="col-sm-6">
                                                        <form>
                                                            <input type="hidden" value="autocompleteworkaround" />
                                                            <div className="form-group row">
                                                                <h6 className="col-12"><i className="material-icons text-success pr-2 mr-2">Decisioned</i> Structure</h6>
                                                            </div>
                                                        </form>
                                                    </div>
                                                </div>
                                                
                                                <div className="row">
                                                    <div className="col-sm-6">
                                                        <form className="h-100">
                                                            {/* Requested Structure */}
                                                            <div className="form-group row">
                                                                <label htmlFor="req_sales_price" className="col-5 col-form-label">
                                                                    Sales Price &nbsp;
                                                                    <div className="popup" onClick={() => fancyTooltip('reqSalesPriceLabel')}>
                                                                        <div className="question_mark">
                                                                            <i style={{color: "#fff"}} className="fa-solid fa-question"></i>
                                                                        </div>
                                                                        <span className="popuptext" id="reqSalesPriceLabel">
                                                                            Sales Price includes TAX, TTL & Fees
                                                                        </span>
                                                                    </div>
                                                                </label>
                                                                <div className="col-5 mt-1 mb-1">
                                                                    <input 
                                                                        id="req_sales_price" 
                                                                        name="req_sales_price" 
                                                                        type="number" 
                                                                        min="0" 
                                                                        className="form-control itemization input_height" 
                                                                        autoComplete="off" 
                                                                        value={appData.req_sales_price} 
                                                                        onChange={handleChange} 
                                                                        readOnly={fundingApplication || submittedApplication} 
                                                                    />
                                                                </div>
                                                            </div>
                                                            
                                                            <div className="form-group row">
                                                                <label htmlFor="req_cash_down" className="col-5 col-form-label">Cash Down Payment</label>
                                                                <div className="col-5 mt-1 mb-1">
                                                                    <input 
                                                                        id="req_cash_down" 
                                                                        name="req_cash_down" 
                                                                        type="number" 
                                                                        min="0" 
                                                                        className="form-control itemization input_height" 
                                                                        autoComplete="off" 
                                                                        value={appData.req_cash_down} 
                                                                        onChange={handleChange} 
                                                                        readOnly={fundingApplication || submittedApplication} 
                                                                    />
                                                                </div>
                                                            </div>
                                                            
                                                            <div className="form-group row">
                                                                <label htmlFor="req_net_trade" className="col-5 col-form-label">Trade-In Credit</label>
                                                                <div className="col-5 mt-1 mb-1">
                                                                    <input 
                                                                        id="req_net_trade" 
                                                                        name="req_net_trade" 
                                                                        type="number" 
                                                                        min="0" 
                                                                        className="form-control itemization input_height" 
                                                                        autoComplete="off" 
                                                                        value={appData.req_net_trade} 
                                                                        onChange={handleChange} 
                                                                        readOnly={fundingApplication || submittedApplication} 
                                                                    />
                                                                </div>
                                                            </div>
                                                            
                                                            <div className="form-group row">
                                                                <label htmlFor="req_unpaid_balance" className="col-5 col-form-label">Max Advance</label>
                                                                <div className="col-5 mt-1 mb-1">
                                                                    <input 
                                                                        id="req_unpaid_balance" 
                                                                        name="req_unpaid_balance" 
                                                                        type="text" 
                                                                        className="form-control itemization input_height" 
                                                                        value={calculatedMaxAdvance ? calculatedMaxAdvance : ''} 
                                                                        readOnly 
                                                                    />
                                                                </div>
                                                            </div>
                                                            
                                                            {appData.rec_buyer_tier === 'PREM' && (
                                                                <div className="form-group row">
                                                                    <label htmlFor="req_backend_allowance" className="col-5 col-form-label">Max Back End</label>
                                                                    <div className="col-5 mt-1 mb-1">
                                                                        <input 
                                                                            id="req_backend_allowance" 
                                                                            name="req_backend_allowance" 
                                                                            type="text" 
                                                                            className="form-control itemization input_height" 
                                                                            autoComplete="off" 
                                                                            value={appData.req_backend_allowance !== '0' ? appData.req_backend_allowance : '0.00'} 
                                                                            onChange={handleChange} 
                                                                            readOnly={fundingApplication || submittedApplication} 
                                                                        />
                                                                    </div>
                                                                </div>
                                                            )}
                                                            
                                                            <div className="form-group row">
                                                                <label htmlFor="req_gap" className="col-5 col-form-label">Gap</label>
                                                                <div className="col-5 mt-1 mb-1">
                                                                    <input 
                                                                        id="req_gap" 
                                                                        name="req_gap" 
                                                                        type="number" 
                                                                        min="0" 
                                                                        className="form-control itemization input_height" 
                                                                        autoComplete="off" 
                                                                        value={appData.req_gap ? appData.req_gap : ''} 
                                                                        onChange={handleChange} 
                                                                        readOnly={fundingApplication || submittedApplication} 
                                                                    />
                                                                </div>
                                                            </div>
                                                            
                                                            {dealerState === 'FL' && (
                                                                <div className="form-group row">
                                                                    <label htmlFor="staticEmail" className="col-5 col-form-label">FL Doc Stamp Tax</label>
                                                                    <div className="col-5 mt-1 mb-1">
                                                                        <input 
                                                                            type="number" 
                                                                            min="0" 
                                                                            className="form-control itemization input_height" 
                                                                            defaultValue={numberWithCommas(appData.rec_credit_life_ins)} 
                                                                            readOnly 
                                                                        />
                                                                    </div>
                                                                </div>
                                                            )}
                                                            
                                                            <div className="form-group row">
                                                                <label htmlFor="req_amt_financed" className="col-5 col-form-label">Amount Financed</label>
                                                                <div className="col-5 mt-1 mb-1">
                                                                    <input 
                                                                        id="req_amt_financed" 
                                                                        name="req_amt_financed" 
                                                                        type="text" 
                                                                        className="form-control itemization input_height" 
                                                                        value={calculatedAmountFinanced ? calculatedAmountFinanced : ''} 
                                                                        readOnly 
                                                                    />
                                                                </div>
                                                            </div>

                                                            <hr style={{ marginTop: "16px" }} />

                                                            <div className="form-group row">
                                                                <label htmlFor="req_term_months" className="col-5 col-form-label">Monthly Term</label>
                                                                <div className="col-5 mt-1 mb-1">
                                                                    <input 
                                                                        id="req_term_months" 
                                                                        name="req_term_months" 
                                                                        type="number" 
                                                                        min="0" 
                                                                        className="form-control itemization input_height" 
                                                                        autoComplete="off" 
                                                                        value={appData.req_term_months} 
                                                                        onChange={handleChange} 
                                                                        readOnly={fundingApplication || submittedApplication} 
                                                                    />
                                                                </div>
                                                            </div>
                                                            
                                                            <div className="form-group row">
                                                                <label htmlFor="req_rate" className="col-5 col-form-label">Rate</label>
                                                                <div className="col-5 mt-1 mb-1">
                                                                    <input 
                                                                        id="req_rate" 
                                                                        name="req_rate" 
                                                                        type="number" 
                                                                        min="0" 
                                                                        className="form-control itemization input_height" 
                                                                        autoComplete="off" 
                                                                        value={appData.req_rate ? appData.req_rate : ''} 
                                                                        onChange={handleChange} 
                                                                        readOnly={fundingApplication || submittedApplication} 
                                                                    />
                                                                </div>
                                                            </div>
                                                            
                                                            <div className="form-group row">
                                                                <label htmlFor="rec_payment_freq" className="col-5 col-form-label">Payment Frequency</label>
                                                                <div className="col-5 mt-1 mb-1">
                                                                    <select 
                                                                        id="rec_payment_freq" 
                                                                        name="rec_payment_freq" 
                                                                        className="form-select dropdown" 
                                                                        aria-label="Default select example" 
                                                                        value={appData.rec_payment_freq} 
                                                                        onChange={handleSelectChange} 
                                                                        disabled={fundingApplication || submittedApplication}
                                                                    >
                                                                        <option value="Bi Weekly">Bi Weekly</option>
                                                                        <option value="Monthly">Monthly</option>
                                                                        <option value="Semi Monthly">Semi Monthly</option>
                                                                        <option value="Weekly">Weekly</option>
                                                                    </select>
                                                                </div>
                                                            </div>
                                                            
                                                            <div className="form-group row">
                                                                <label htmlFor="req_payment" className="col-5 col-form-label">Payment</label>
                                                                <div className="col-5 mt-1 mb-1">
                                                                    <input 
                                                                        id="req_payment" 
                                                                        name="req_payment" 
                                                                        type="number" 
                                                                        min="0" 
                                                                        className="form-control itemization input_height" 
                                                                        autoComplete="off" 
                                                                        value={appData.req_payment ? appData.req_payment : ''} 
                                                                        onChange={handleChange} 
                                                                        readOnly={fundingApplication || submittedApplication} 
                                                                    />
                                                                </div>
                                                            </div>
                                                            
                                                            <hr />
                                                            
                                                            <div className="form-group row">
                                                                <div className="col-12 text-center">
                                                                    <button 
                                                                        type="button" 
                                                                        className="btn btn-success mx-3" 
                                                                        onClick={() => updateApplication(false)} 
                                                                        data-bs-toggle="modal" 
                                                                        data-bs-target="#post_submit_modal"
                                                                        disabled={fundingApplication || submittedApplication || !modifiedApplication || !appData.rec_application_number}
                                                                    >
                                                                        Rehash
                                                                    </button>
                                                                    &nbsp;&nbsp;
                                                                    {
                                                                        fundingApplication ? <><br /><br />This application is now being funded.</> :
                                                                            submittedApplication ? <><br /><br />This application has been submitted.</> :
                                                                                !modifiedApplication ? <><br /><br />You have not yet modified this application.</> : ''
                                                                    }
                                                                </div>
                                                            </div>
                                                        </form>
                                                    </div>
                                                    
                                                    <div className="col-sm-6">
                                                        <form className="h-100">
                                                            {/* Decisioned Structure */}
                                                            <div className="form-group row">
                                                                <label htmlFor="staticEmail" className="col-5 col-form-label">
                                                                    Sales Price &nbsp;
                                                                    <div className="popup" onClick={() => fancyTooltip('recSalesPriceLabel')}>
                                                                        <div className="question_mark">
                                                                            <i style={{ color: "#fff" }} className="fa-solid fa-question"></i>
                                                                        </div>
                                                                        <span className="popuptext" id="recSalesPriceLabel">
                                                                            Sales Price includes TAX, TTL & Fees
                                                                        </span>
                                                                    </div>
                                                                </label>
                                                                <div className="col-7 text-right">
                                                                    <p className="mt-2">
                                                                        {submittedApplication ? 'Pending' :
                                                                        (appData.rec_app_status === 'AD' || appData.rec_app_status === 'Declined') ? '-' :
                                                                        '$' + numberWithCommas(appData.rec_sales_price)}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            
                                                            <div className="form-group row">
                                                                <label htmlFor="staticEmail" className="col-5 col-form-label">Cash Down Payment</label>
                                                                <div className="col-7 text-right">
                                                                    <p className="mt-2">
                                                                        {submittedApplication ? 'Pending' :
                                                                        (appData.rec_app_status === 'AD' || appData.rec_app_status === 'Declined') ? '-' :
                                                                        '$' + numberWithCommas(appData.rec_cash_down)}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            
                                                            <div className="form-group row">
                                                                <label htmlFor="staticEmail" className="col-5 col-form-label">Trade-In Credit</label>
                                                                <div className="col-7 text-right">
                                                                    <p className="mt-2">
                                                                        {submittedApplication ? 'Pending' :
                                                                        (appData.rec_app_status === 'AD' || appData.rec_app_status === 'Declined') ? '-' :
                                                                        '$' + numberWithCommas(appData.rec_net_trade)}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            
                                                            <div className="form-group row">
                                                                <label htmlFor="staticEmail" className="col-5 col-form-label">Max Advance</label>
                                                                <div className="col-7 text-right">
                                                                    <p className="mt-2">
                                                                        {submittedApplication ? 'Pending' :
                                                                        (appData.rec_app_status === 'AD' || appData.rec_app_status === 'Declined') ? '-' :
                                                                        '$' + numberWithCommas(appData.rec_unpaid_balance)}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            
                                                            {appData.rec_buyer_tier === 'PREM' && (
                                                                <div className="form-group row">
                                                                    <label htmlFor="staticEmail" className="col-5 col-form-label">Max Back End</label>
                                                                    <div className="col-7 text-right">
                                                                        <p className="mt-2">
                                                                            {submittedApplication ? 'Pending' :
                                                                            (appData.rec_app_status === 'AD' || appData.rec_app_status === 'Declined') ? '-' :
                                                                            '$' + numberWithCommas(appData.rec_backend_allowance)}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            )}
                                                            
                                                            <div className="form-group row">
                                                                <label htmlFor="staticEmail" className="col-5 col-form-label">Gap</label>
                                                                <div className="col-7 text-right">
                                                                    <p className="mt-2">
                                                                        {submittedApplication ? 'Pending' :
                                                                        (appData.rec_app_status === 'AD' || appData.rec_app_status === 'Declined') ? '-' :
                                                                        '$' + numberWithCommas(appData.rec_gap)}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            
                                                            {dealerState === 'FL' && (
                                                                <div className="form-group row">
                                                                    <label htmlFor="staticEmail" className="col-5 col-form-label">FL Doc Stamp Tax</label>
                                                                    <div className="col-7 text-right">
                                                                        <p className="mt-2">
                                                                            {submittedApplication ? 'Pending' :
                                                                            (appData.rec_app_status === 'AD' || appData.rec_app_status === 'Declined') ? '-' :
                                                                            '$' + numberWithCommas(appData.rec_credit_life_ins)}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            )}
                                                            
                                                            <div className="form-group row">
                                                                <label htmlFor="staticEmail" className="col-5 col-form-label">Amount Financed</label>
                                                                <div className="col-7 text-right">
                                                                    <p className="mt-2">
                                                                        {submittedApplication ? 'Pending' :
                                                                        (appData.rec_app_status === 'AD' || appData.rec_app_status === 'Declined') ? '-' :
                                                                        '$' + numberWithCommas(appData.rec_est_amt_financed)}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            
                                                            <hr style={{ marginTop: "16px" }} />
                                                            
                                                            <div className="form-group row">
                                                                <label htmlFor="staticEmail" className="col-5 col-form-label">Monthly Term</label>
                                                                <div className="col-7 text-right">
                                                                    <p className="mt-2">
                                                                        {submittedApplication ? 'Pending' :
                                                                        (appData.rec_app_status === 'AD' || appData.rec_app_status === 'Declined') ? '-' :
                                                                        appData.rec_term_months + ' months'}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            
                                                            <div className="form-group row">
                                                                <label htmlFor="staticEmail" className="col-5 col-form-label">Rate</label>
                                                                <div className="col-7 text-right">
                                                                    <p className="mt-2">
                                                                        {submittedApplication ? 'Pending' :
                                                                        (appData.rec_app_status === 'AD' || appData.rec_app_status === 'Declined') ? '-' :
                                                                        appData.rec_buyrate + '%'}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            
                                                            <div className="form-group row">
                                                                <label htmlFor="staticEmail" className="col-5 col-form-label">Payment Frequency</label>
                                                                <div className="col-7 text-right">
                                                                    <p className="mt-2">
                                                                        {submittedApplication ? 'Pending' :
                                                                        (appData.rec_app_status === 'AD' || appData.rec_app_status === 'Declined') ? '-' :
                                                                        appData.rec_payment_freq}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            
                                                            <div className="form-group row">
                                                                <label htmlFor="staticEmail" className="col-5 col-form-label">Payment</label>
                                                                <div className="col-7 text-right">
                                                                    <p className="mt-2">
                                                                        {submittedApplication ? 'Pending' :
                                                                        (appData.rec_app_status === 'AD' || appData.rec_app_status === 'Declined') ? '-' :
                                                                        '$' + numberWithCommas(appData.rec_payment)}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            
                                                            <hr />
                                                            
                                                            <div className="form-group row">
                                                                <label htmlFor="staticEmail" className="col-5 col-form-label">Discount</label>
                                                                <div className="col-7 text-right">
                                                                    <p className="mt-2">
                                                                        {submittedApplication ? 'Pending' :
                                                                        (appData.rec_app_status === 'AD' || appData.rec_app_status === 'Declined') ? '-' :
                                                                        '$' + numberWithCommas(appData.rec_calc_discount)}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            
                                                            <div className="form-group row">
                                                                <label htmlFor="staticEmail" className="col-5 col-form-label">Origination Fee</label>
                                                                <div className="col-7 text-right">
                                                                    <p className="mt-2">
                                                                        {submittedApplication ? 'Pending' :
                                                                        (appData.rec_app_status === 'AD' || appData.rec_app_status === 'Declined') ? '-' :
                                                                        '$' + (appData.rec_orig_fee ? parseFloat(appData.rec_orig_fee).toFixed(2) : '0.00')}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            
                                                            <div className="form-group row">
                                                                <label htmlFor="staticEmail" className="col-5 col-form-label">Net Check</label>
                                                                <div className="col-7 text-right">
                                                                    <p className="mt-2 dealer_proceeds">
                                                                        {submittedApplication ? 'Pending' :
                                                                        (appData.rec_app_status === 'AD' || appData.rec_app_status === 'Declined') ? '-' :
                                                                        <b>${numberWithCommas(appData.rec_dealer_proceeds)}</b>}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </form>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-xl-4">
                        {/* Account details card */}
                        <div className="card mb-2">
                            <div className="card h-100 border-start-lg border-start-success">
                                <div className="card-body">
                                    <div className="h3 d-flex align-items-center">Status: &nbsp;
                                        {
                                            submittedApplication ? <Feather color="blue" /> :
                                                (acceptedApplication || appData.rec_app_status === 'Approved' || appData.rec_app_status === 'Conditioned') ? <CheckCircle color="limegreen" /> :
                                                    (appData.rec_app_status === 'AD' || appData.rec_app_status === 'Declined') ? <XOctagon color="red" /> :
                                                        <Feather color="green" />
                                        }
                                        &nbsp;
                                        {
                                            fundingStatusArray.includes(appData.rec_app_status) ? appData.rec_app_status :
                                                acceptedApplication ? 'Accepted' :
                                                    (appData.rec_app_status === 'Approved' || appData.rec_app_status === 'Conditioned') && !submittedApplication ? appData.rec_app_status :
                                                        String(appData.rec_AS_indicator).includes('Rehash') ? 'Under Review' :
                                                            submittedApplication ? 'Underwriting in Progress' :
                                                                appData.rec_app_status === 'AD' ? 'Auto-Declined' :
                                                                    appData.rec_app_status
                                        }
                                    </div>
                                    <div className="small text-dark">
                                        Application Number: &nbsp;
                                        {appData.rec_application_number}
                                    </div>
                                    <div className="small text-dark">Program Qualification: &nbsp; {appData.rec_buyer_tier}</div>
                                    <div className="small text-success"><b>Dealer Proceeds:</b> &nbsp;
                                        <b>
                                            {submittedApplication ? "Pending" : "$" +
                                                ((appData.rec_app_status === 'AD' || appData.rec_app_status === 'Declined') ? '0' : numberWithCommas(appData.rec_dealer_proceeds))
                                            }
                                        </b>
                                    </div>
                                    {submittedApplication ? '' :
                                        <a href="/#" className="small text-primary" data-bs-toggle="modal"
                                            data-bs-target=
                                            {appData.rec_app_status === 'AD' || appData.rec_app_status === 'Declined' ? '#decline_callback_modal' :
                                                fundingApplication ? '#stip_sheet_modal' :
                                                    '#approval_callback_modal'}>
                                            <b>{fundingApplication ? 'See Stip Sheet' : 'See Call Back'}</b>
                                        </a>}
                                </div>
                            </div>
                        </div>

                        <div className="col-xl-12">
                            {/* Customer Information */}
                            <div className="card mb-2">
                                <div className="card-header">
                                    Customer Information
                                    {showProgress ? <img src="/images/rainbow-spinner.gif" alt="spinner" height="20px" id="progressindicator" style={{ marginLeft: "5px" }} /> : <></>}
                                    <button className="btn btn-sm btn-primary str_btn" style={{ float: "right" }} type="button" data-bs-toggle="modal" data-bs-target="#customer_modal" disabled={fundingApplication || submittedApplication || !appData.rec_application_number}>Edit</button>
                                </div>
                                <div className="card-body">
                                    <div className="row">
                                        <div className="col-6">
                                            <label className="large mb-1 vehicle_label">Applicant Name:</label>
                                            <p className="">{appData.rec_app_first_name} {appData.rec_app_last_name}</p>
                                        </div>
                                        <div className="col-6">
                                            <label className="large mb-1 vehicle_label">Co-Applicant Name:</label>
                                            <p className="">{appData.rec_coapp_first_name} {appData.rec_coapp_last_name}</p>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-6">
                                            <label className="large mb-1 vehicle_label">Employer Name:</label>
                                            <p className="">{appData.rec_app_emp_name}</p>
                                        </div>
                                        <div className="col-6">
                                            <label className="large mb-1 vehicle_label">Employer Name:</label>
                                            <p className="">{appData.rec_coapp_emp_name}</p>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-6">
                                            <label className="large mb-1 vehicle_label">Monthly Income:</label>
                                            <p className="">{appData.rec_app_income ? '$' + numberWithCommas(appData.rec_app_income) : ''}</p>
                                        </div>
                                        <div className="col-6">
                                            <label className="large mb-1 vehicle_label">Monthly Income:</label>
                                            <p className="">{appData.rec_coapp_income ? '$' + numberWithCommas(appData.rec_coapp_income) : ''}</p>
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-6">
                                            <label className="large mb-1 vehicle_label">Months on Job:</label>
                                            <p className="">{appData.rec_app_emp_total_months ? appData.rec_app_emp_total_months + ' months' : ''}</p>
                                        </div>
                                        <div className="col-6">
                                            <label className="large mb-1 vehicle_label">Months on Job:</label>
                                            <p className="">{appData.rec_coapp_emp_total_months ? appData.rec_coapp_emp_total_months + ' months' : ''}</p>
                                        </div>
                                    </div>
                                </div>

                            </div>

                            {/* Vehicle Information */}
                            <div className="card mb-1">
                                <div className="card-header">
                                    Vehicle Information
                                    {showProgress ? <img src="/images/rainbow-spinner.gif" alt="spinner" height="20px" id="progressindicator" style={{ marginLeft: "5px" }} /> : ''}
                                    <button className="btn btn-sm btn-primary str_btn" style={{ float: "right" }} type="button" data-bs-toggle="modal" data-bs-target="#vehicle_modal" disabled={fundingApplication || submittedApplication || !appData.rec_application_number}>Edit</button>
                                </div>
                                <div className="card-body">
                                    <div className="row gx-3 ">
                                        <div className="col-md-6">
                                            <label className="large  mb-1 vehicle_label">Vehicle</label>
                                            <p>{appData.rec_UW_collateral_desc}</p>
                                        </div>
                                        <div className="col-md-6">
                                            <label className="large mb-1 vehicle_label">Mileage</label>
                                            <p>{numberWithCommas(appData.rec_veh_mileage)} miles</p>
                                        </div>
                                        <div className="col-md-6">
                                            <label className="large mb-1 vehicle_label">VIN</label>
                                            <p>{appData.rec_vin}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Stipulations */}
                            <div className="card mb-1">
                                <div className="card-header">Stipulations</div>
                                <div className="card-body">
                                    <ul>
                                        {dealStips.map((dealStip, index) => {
                                            if (String(dealStip).includes('|')) {
                                                const stipParts = String(dealStip).split('|');
                                                const stipName = stipParts[0];
                                                const stipStatus = stipParts[1];
                                                const stipType = stipParts[2];
                                                if (['Failed', 'Incomplete'].includes(stipStatus) && ['UW', 'VF'].includes(stipType)) {
                                                    return <li key={index}>{stipName}</li>;
                                                }
                                                return null;
                                            } else {
                                                return <li key={index}>{dealStip}</li>;
                                            }
                                        })}
                                    </ul>
                                </div>
                            </div>

                            {/* Notes to Dealer */}
                            <div className="card mb-1">
                                <div className="card-header">Tracir Financial Services Notes to Dealer</div>
                                <div className="card-body">
                                    <ul>
                                        {dealerNotes.map((dealerNote, index) => (
                                            <li key={index}>
                                                {dealerNote}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                    <span className="mt-0 mb-4"></span>
                </div>
            </div>

            {/* Render all modals */}
            <AcceptModal dealerEmail={appData.rec_dealer_email} />
            
            <ApprovalCallbackModal 
                appData={appData} 
                numberWithCommas={numberWithCommas} 
                dealStips={dealStips} 
                dealerNotes={dealerNotes} 
            />
            
            <DeclineCallbackModal 
                appData={appData} 
                numberWithCommas={numberWithCommas} 
                dealerNotes={dealerNotes} 
            />
            
            <StipSheetModal 
                appData={appData} 
                numberWithCommas={numberWithCommas} 
                dealStips={dealStips} 
            />
            
            <CustomerInfoModal 
                appData={appData} 
                handleChange={handleChange} 
                handleSelectChange={handleSelectChange} 
            />
            
            <PostSubmitModal serverMessage={serverMessage} />
            
            <PreviousStructuresModal />
            
            <VehicleInfoModal 
                currentVin={currentVin}
                currentMileage={currentMileage}
                currentVehicleDescription={currentVehicleDescription}
                newVehicleYear={newVehicleYear}
                newVehicleMake={newVehicleMake}
                newVehicleModel={newVehicleModel}
                modifiedVehicle={modifiedVehicle}
                handleChange={handleChange}
            />
            
            <ChatModal 
                appNum={appNum}
                chatMessages={chatMessages}
                setChatMessages={setChatMessages}
                userId={userId}
                firstName={firstName}
                lastName={lastName}
                dealerId={dealerId}
                token={token}
                config={config}
            />
            
            <ContactUsModal />
        </>
    );
};

export default Rehash;
